import { isPlatformServer } from '@angular/common';
import { DOCUMENT, effect, inject, injectAsync, onIdle, PLATFORM_ID, Service, signal } from '@angular/core';
import { environment } from '@workspace/ng-config';
import { Scheme, SessionStorageKey } from '@workspace/ng-data-access';
import { CookieService } from 'ngx-cookie-service';
import { Media } from '../media';
import { Palette, Palettes, Theme } from './models/theming';
import { THEME_CONFIG } from './provider';

@Service()
export class Theming {
  // Dependencies
  private document = inject(DOCUMENT);
  private isServer = isPlatformServer(inject(PLATFORM_ID));
  private media = inject(Media);
  private themeConfig = inject(THEME_CONFIG);
  private cookieService = inject(CookieService);
  private paletteService = injectAsync(() => import('./palette.service').then((m) => m.ThemePaletteService), {
    prefetch: onIdle,
  });

  // State
  private prefersDarkMode = this.media.match('(prefers-color-scheme: dark)');

  theme = signal<Theme>({
    primary: this.themeConfig.primary,
    error: this.themeConfig.error,
    neutral: this.themeConfig.neutral,
  });
  scheme = signal<Scheme>(
    (this.cookieService.get(SessionStorageKey.SCHEME) as Scheme) || this.themeConfig.scheme,
  );
  palettes = signal<Palettes | undefined>(undefined);

  // DOM
  private themeStyleEl = this.isServer ? null : this.document.createElement('style');

  // Effects
  private schemeUpdateEffect = effect(() => {
    // Skip the server. Otherwise, the scheme will always be 'system' and
    // the class will always be 'scheme-dark'.
    if (this.isServer) {
      return;
    }

    const scheme = this.scheme();
    const prefersDarkMode = this.prefersDarkMode();

    // Figure out if the scheme is 'dark'
    const isDark = scheme === 'dark' || (scheme === 'system' && prefersDarkMode);

    const rootEl = this.document.body;
    if (rootEl) {
      // Add the 'dark' or 'light' class to the html and body elements
      rootEl.classList.toggle('scheme-dark', isDark);
      rootEl.classList.toggle('scheme-light', !isDark);
      this.document.documentElement.classList.toggle('scheme-dark', isDark);
      this.document.documentElement.classList.toggle('scheme-light', !isDark);
    }

    // Store the scheme in local storage
    this.cookieService.set(SessionStorageKey.SCHEME, scheme, 365, '/', `.${environment.domain}`, true, 'Lax');
  });

  private paletteGenerationEffect = effect(() => {
    // Track theme changes
    const theme = this.theme();
    // Trigger async palette generation
    void this.generatePalettes(theme);
  });

  private palettesUpdateEffect = effect(() => {
    // Apply palettes when they are ready
    const palettes = this.palettes();
    if (palettes) {
      this.applyPalettes(palettes);
    }
  });

  constructor() {
    if (this.isServer) {
      return;
    }
    // Append the themeStyleEl to the DOM if it's not already there
    if (this.themeStyleEl && !this.document.head.querySelector('.theme-colors')) {
      this.document.head.appendChild(this.themeStyleEl);
      this.themeStyleEl.classList.add('theme-colors');
    }
  }

  /**
   * Generate palettes from the theme config.
   * Async because generatePalette lazily loads reference-colors + colorjs.io.
   */
  private async generatePalettes(config: Theme): Promise<void> {
    const paletteSvc = await this.paletteService();
    const [light, dark] = await Promise.all([
      paletteSvc.generate({
        appearance: 'light',
        primary: config.primary,
        error: config.error,
        neutral: config.neutral,
        background: '#FFFFFF',
      }),
      paletteSvc.generate({
        appearance: 'dark',
        primary: config.primary,
        error: config.error,
        neutral: config.neutral,
        background: '#111111',
      }),
    ]);

    this.palettes.set({
      source: {
        primary: config.primary,
        error: config.error,
        neutral: config.neutral,
      },
      light,
      dark,
    });
  }

  /**
   * Applies generated color palettes to the DOM by injecting CSS variables
   * into the style element.
   */
  private applyPalettes(palettes: Palettes) {
    if (this.isServer || !this.themeStyleEl) {
      return;
    }
    const variablePrefix = 'theme';
    const style = this.themeStyleEl;

    // Build the style content
    style.textContent = `:root {`;

    // Primary
    const primaryKeys = Object.keys(palettes.light.primary) as (keyof Palette['primary'])[];
    for (const name of primaryKeys) {
      const lightColor = palettes.light.primary[name];
      const darkColor = palettes.dark.primary[name];
      style.textContent += `--${variablePrefix}-color-primary-${name}: light-dark(${lightColor}, ${darkColor});`;
    }

    // Error
    const errorKeys = Object.keys(palettes.light.error) as (keyof Palette['error'])[];
    for (const name of errorKeys) {
      const lightColor = palettes.light.error[name];
      const darkColor = palettes.dark.error[name];
      style.textContent += `--${variablePrefix}-color-error-${name}: light-dark(${lightColor}, ${darkColor});`;
    }

    // Neutral
    const neutralKeys = Object.keys(palettes.light.neutral) as (keyof Palette['neutral'])[];
    for (const name of neutralKeys) {
      const lightColor = palettes.light.neutral[name];
      const darkColor = palettes.dark.neutral[name];
      style.textContent += `--${variablePrefix}-color-neutral-${name}: light-dark(${lightColor}, ${darkColor});`;
    }

    style.textContent += `}`;
  }
}
